import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Download, AlertCircle, CheckCircle2, Building2, FileText, Wallet,
  Loader2,
} from "lucide-react";

/**
 * Phase 5N — Public Payslip Viewer (no auth)
 * Accessed via /p/payslip/:token
 *
 * Shows: company header + employee summary + salary breakdown + Download button.
 * On Download → records view + consumes one-time link.
 */
export default function PublicPayslip() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API}/api/public/payslip/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.expired) setError({ status: d.status, message: d.message });
        else setData(d);
      })
      .catch(() => setError({ status: "error", message: "Unable to load payslip" }))
      .finally(() => setLoading(false));
  }, [token, API]);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(`${API}/api/public/payslip/${token}/pdf`);
      if (!resp.ok) {
        const t = await resp.text();
        setError({ status: "error", message: `Failed: ${t}` });
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip_${data?.employee_code || token}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      // If one-time, refresh status
      if (data?.one_time) {
        setError({
          status: "used",
          message: "This was a one-time link. The PDF has been downloaded — refresh this page later to see the 'used' state.",
        });
        setData(null);
      }
    } catch {
      setError({ status: "error", message: "Network error" });
    } finally { setDownloading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-red-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white border border-amber-200 rounded-lg p-6 shadow-sm" data-testid="payslip-expired">
          <AlertCircle className="h-10 w-10 text-amber-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-slate-600">{error.message}</p>
          <p className="text-xs text-slate-400 mt-4">
            If you need a new link, please contact your HR team.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary || {};
  const earnings = [
    ["Basic", s.basic],
    ["HRA", s.hra],
    ["Conveyance", s.conveyance],
    ["DA", s.da],
    ["Statutory Bonus (8.33%)", s.statutory_bonus],
    ["OT Earning", s.ot_earning],
    ["Incentive", s.incentive],
    ["Performance Bonus", s.p_bonus],
  ].filter(([, v]) => v && Number(v) > 0);

  const deductions = [
    ["PF (Employee)", s.pf_employee],
    ["ESI (Employee)", s.esi_employee],
    ["Professional Tax", s.professional_tax],
    ["Advance", s.advance],
    ["TDS", s.tds],
    ["Other Deductions", s.other_deductions],
  ].filter(([, v]) => v && Number(v) > 0);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8 px-2 sm:px-4" data-testid="public-payslip">
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        {/* Header — pink brand */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded bg-white p-1 flex-shrink-0">
              <img src="/bravura_logo.png" alt="Bravura" className="h-full w-full object-contain" />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-bold leading-tight">Bravura Packers Pvt Ltd</h1>
              <p className="text-[11px] sm:text-xs opacity-90">Jaipur, Rajasthan</p>
            </div>
          </div>
          <div className="mt-3 text-xs uppercase tracking-wider opacity-90">Salary Slip · {data.month_label}</div>
        </div>

        {/* Employee */}
        <div className="px-5 py-4 grid grid-cols-2 gap-3 text-xs border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase text-slate-400">Employee</div>
            <div className="font-bold text-slate-900 text-sm">{data.employee_name}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase text-slate-400">Code</div>
            <div className="font-mono text-slate-700">{data.employee_code}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400">Unit</div>
            <div className="text-slate-700"><Building2 className="h-3 w-3 inline mr-1" />{data.unit_name || "—"}</div>
          </div>
        </div>

        {/* NET Salary big tile */}
        <div className="px-5 py-5 bg-emerald-50 border-b border-emerald-100">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 opacity-80">Net Salary</div>
          <div className="text-3xl sm:text-4xl font-bold text-emerald-700 mt-1" data-testid="net-salary">
            {fmt(s.net_salary)}
          </div>
          <div className="text-xs text-slate-600 mt-1">
            Gross {fmt(s.gross_salary)} − Deductions {fmt(s.total_deductions)}
          </div>
        </div>

        {/* Earnings / Deductions grid */}
        <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
          <div className="p-5">
            <h3 className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-3 flex items-center gap-1">
              <Wallet className="h-3 w-3" />Earnings
            </h3>
            <div className="space-y-2">
              {earnings.map(([label, val]) => (
                <div key={label} className="flex justify-between text-xs">
                  <span className="text-slate-600">{label}</span>
                  <span className="font-mono font-medium text-slate-900">{fmt(val)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm border-t border-slate-200 pt-2 mt-3">
                <span className="font-bold text-slate-900">Gross</span>
                <span className="font-mono font-bold text-emerald-700">{fmt(s.gross_salary)}</span>
              </div>
            </div>
          </div>

          <div className="p-5">
            <h3 className="text-[10px] uppercase tracking-widest text-red-700 font-bold mb-3">Deductions</h3>
            <div className="space-y-2">
              {deductions.length === 0 && (
                <p className="text-[11px] text-slate-400 italic">No deductions</p>
              )}
              {deductions.map(([label, val]) => (
                <div key={label} className="flex justify-between text-xs">
                  <span className="text-slate-600">{label}</span>
                  <span className="font-mono font-medium text-slate-900">{fmt(val)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm border-t border-slate-200 pt-2 mt-3">
                <span className="font-bold text-slate-900">Total</span>
                <span className="font-mono font-bold text-red-700">{fmt(s.total_deductions)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Download CTA */}
        <div className="p-5 bg-slate-50 border-t border-slate-200">
          <Button
            onClick={downloadPDF}
            disabled={downloading}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-base font-semibold"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Generating PDF…</>
            ) : (
              <><Download className="h-5 w-5 mr-2" />Download Full Payslip PDF</>
            )}
          </Button>
          {data.one_time && (
            <p className="text-[11px] text-amber-700 mt-2 text-center flex items-center justify-center gap-1">
              <AlertCircle className="h-3 w-3" />
              This is a one-time link. Save the PDF after downloading.
            </p>
          )}
          <p className="text-[10px] text-slate-400 text-center mt-3">
            <FileText className="h-3 w-3 inline mr-0.5" />
            This is a system-generated payslip. For queries, contact HR.
          </p>
        </div>
      </div>
    </div>
  );
}
